import {
  createRazorpayOrder,
  verifyRazorpayPayment,
  CreateRazorpayOrderRequest,
  RazorpayOrderResponse,
} from "../api/paymentApi";
import { saveStudentPayment } from "../api/studentApi";
import { RazorpaySuccessResponse } from "../types/razorpay.d";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

let scriptLoadPromise: Promise<void> | null = null;

const loadRazorpayScript = (): Promise<void> => {
  if (window.Razorpay) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${RAZORPAY_SCRIPT_URL}"]`
    );

    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Unable to load Razorpay checkout."))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Unable to load Razorpay checkout."));
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
};

export interface RazorpayCheckoutPrefill {
  name?: string;
  email?: string;
  contact?: string;
}

export interface ProcessRazorpayPaymentOptions {
  orderRequest?: CreateRazorpayOrderRequest;
  existingOrder?: RazorpayOrderResponse;
  description: string;
  prefill?: RazorpayCheckoutPrefill;
  purpose?: "membership" | "registration";
  onDismiss?: () => void;
}

export const processRazorpayPayment = async ({
  orderRequest,
  existingOrder,
  description,
  prefill,
  purpose,
  onDismiss,
}: ProcessRazorpayPaymentOptions): Promise<void> => {
  const orderResponse = existingOrder
    ? { success: true, data: existingOrder }
    : orderRequest
      ? await createRazorpayOrder(orderRequest)
      : null;

  if (!orderResponse?.success || !orderResponse.data) {
    throw new Error(
      (orderResponse && "message" in orderResponse
        ? orderResponse.message || orderResponse.errors
        : null) || "Unable to start payment."
    );
  }

  await loadRazorpayScript();

  if (!window.Razorpay) {
    throw new Error("Razorpay checkout is unavailable.");
  }

  const order = orderResponse.data;

  await new Promise<void>((resolve, reject) => {
    const checkout = new window.Razorpay!({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      name: order.companyName || "Om Sai Reading Room",
      description,
      order_id: order.orderId,
      prefill,
      theme: { color: "#0b5ed7" },
      handler: async (response: RazorpaySuccessResponse) => {
        try {
          const verifyResponse = await verifyRazorpayPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });

          if (!verifyResponse.success || !verifyResponse.data) {
            throw new Error(
              verifyResponse.message ||
                verifyResponse.errors ||
                "Payment verification failed."
            );
          }

          const verified = verifyResponse.data;
          const paymentPurpose = purpose ?? orderRequest?.purpose;

          if (
            verifyResponse.message !== "Payment already recorded." &&
            verified.studentDetailsId > 0
          ) {
            const saveResponse = await saveStudentPayment({
              studentDetailsId: verified.studentDetailsId,
              amount: verified.amount,
              validityFrom: verified.validityFrom.slice(0, 10),
              validityTo: verified.validityTo.slice(0, 10),
              razorpayOrderId: verified.razorpayOrderId,
              purpose: paymentPurpose,
            });

            if (!saveResponse.success) {
              throw new Error(
                saveResponse.message ||
                  saveResponse.errors ||
                  "Payment verified but could not be saved."
              );
            }
          }

          if (
            verifyResponse.message !== "Payment already recorded." &&
            verified.studentDetailsId <= 0
          ) {
            throw new Error(
              "Payment succeeded but registration could not be completed. Please contact the library."
            );
          }

          resolve();
        } catch (error) {
          reject(error);
        }
      },
      modal: {
        ondismiss: () => {
          onDismiss?.();
          reject(new Error("Payment cancelled."));
        },
      },
    });

    checkout.on("payment.failed", (response) => {
      reject(
        new Error(
          response.error?.description || "Payment failed. Please try again."
        )
      );
    });

    checkout.open();
  });
};
